import { type HelpContent, type ScreenId } from '@/types/help-support';

export const helpContentMap: Record<ScreenId, HelpContent> = {
  dashboard: {
    title: 'Credit Control Dashboard',
    functionality: {
      overview: 'The Credit Control Dashboard provides a comprehensive real-time overview of your outstanding invoices, collection performance, and key metrics to help you manage accounts receivable effectively.',
      keyFeatures: [
        'Real-time KPI tracking for outstanding amounts and overdue invoices',
        'Visual aging summary showing distribution of outstanding amounts',
        'Recent activity feed for quick updates on collection actions',
        'High-risk customer alerts and prioritization',
        'Collection rate and DSO (Days Sales Outstanding) monitoring',
      ],
    },
    userGuide: {
      gettingStarted: 'The dashboard is your central hub for monitoring credit control activities. Start by reviewing the KPI cards at the top for a quick snapshot of your current position.',
      steps: [
        {
          title: 'Review Key Metrics',
          description: 'Check the KPI cards showing Total Outstanding, Overdue Invoices, Collection Rate, and Average Days Outstanding.',
          tips: [
            'Red indicators show areas requiring immediate attention',
            'Compare current metrics with previous periods using the trend indicators',
          ],
        },
        {
          title: 'Analyze Aging Summary',
          description: 'Review the aging breakdown to understand how your outstanding amounts are distributed across different time periods.',
          tips: [
            'Focus on amounts in 90+ days category for urgent follow-up',
            'Monitor the trend of aging buckets over time',
          ],
        },
        {
          title: 'Monitor Recent Activities',
          description: 'Stay updated with the latest collection activities, promises to pay, and customer interactions.',
        },
        {
          title: 'Identify High-Risk Customers',
          description: 'Review the high-risk customer section to prioritize your collection efforts.',
        },
      ],
    },
    faq: [
      {
        question: 'How often is the dashboard data updated?',
        answer: 'The dashboard data is updated in real-time. You can also manually refresh using the Refresh button in the top-right corner.',
      },
      {
        question: 'What does the Collection Rate percentage mean?',
        answer: 'Collection Rate represents the percentage of invoices successfully collected within their payment terms compared to total invoices issued.',
      },
      {
        question: 'How is the Average Days Outstanding calculated?',
        answer: 'Average Days Outstanding (DSO) is calculated by dividing the total accounts receivable by the average daily sales, providing insight into how quickly you collect payments.',
      },
      {
        question: 'Can I customize which KPIs are displayed?',
        answer: 'Currently, the dashboard shows standard KPIs. Custom dashboard configurations may be available in Settings > Company Settings.',
      },
    ],
  },
  customers: {
    title: 'Customers',
    functionality: {
      overview: 'The Customers page provides a comprehensive view of all your customers, their outstanding balances, credit limits, and risk profiles. Manage customer relationships and track payment behavior efficiently.',
      keyFeatures: [
        'Complete customer list with key financial metrics',
        'Advanced filtering and search capabilities',
        'Risk assessment and credit limit monitoring',
        'Bulk actions for efficient customer management',
        'Quick access to customer details and communication history',
        'Export customer data for reporting',
      ],
    },
    userGuide: {
      gettingStarted: 'Use the Customers page to view and manage all customer accounts. Filter by risk level, outstanding amounts, or payment behavior to prioritize your actions.',
      steps: [
        {
          title: 'Search and Filter Customers',
          description: 'Use the search bar to find specific customers by name or account number. Apply filters to narrow down your list.',
          tips: [
            'Use the risk filter to focus on high-risk customers',
            'Filter by overdue amounts to prioritize collections',
            'Combine multiple filters for precise results',
          ],
        },
        {
          title: 'Review Customer Metrics',
          description: 'Each customer row displays key information including outstanding balance, overdue amount, credit limit, and DSO.',
          tips: [
            'Red indicators show customers exceeding credit limits',
            'Click on any customer row to view detailed information',
          ],
        },
        {
          title: 'Perform Bulk Actions',
          description: 'Select multiple customers to perform actions like assigning collection strategies or sending communications.',
        },
        {
          title: 'Access Customer Details',
          description: 'Click on a customer name to open their detailed profile with full transaction history and communication records.',
        },
      ],
    },
    faq: [
      {
        question: 'How is customer risk calculated?',
        answer: 'Customer risk is calculated based on multiple factors including payment history, overdue amounts, credit utilization, and payment patterns. High-risk customers have significant overdue amounts or poor payment history.',
      },
      {
        question: 'Can I customize the columns displayed?',
        answer: 'Yes, click the Settings icon in the top-right corner to customize which columns are displayed and their order.',
      },
      {
        question: 'How do I create a collection case for a customer?',
        answer: 'Select one or more customers using the checkboxes, then click "Create Case" in the toolbar that appears.',
      },
      {
        question: 'What does DSO mean for each customer?',
        answer: 'DSO (Days Sales Outstanding) for a customer shows the average number of days it takes them to pay their invoices.',
      },
    ],
  },
  'customer-detail': {
    title: 'Customer Details',
    functionality: {
      overview: 'The Customer Details page provides an in-depth view of a specific customer, including their complete transaction history, communication records, payment promises, and aging analysis.',
      keyFeatures: [
        'Comprehensive customer information panel',
        'Transaction history with detailed invoice information',
        'Communication history and activity timeline',
        'Payment promises and commitments tracking',
        'Aging analysis specific to the customer',
        'Contact management and communication tools',
      ],
    },
    userGuide: {
      gettingStarted: 'The Customer Details page is organized into tabs for easy navigation. Start with the Overview tab to see key customer information and metrics.',
      steps: [
        {
          title: 'Review Customer Information',
          description: 'The top panel shows customer details including contact information, credit limit, outstanding balance, and risk level.',
        },
        {
          title: 'Analyze Transactions',
          description: 'Navigate to the Transactions tab to view all invoices, payments, and credits for this customer.',
          tips: [
            'Use filters to find specific transactions',
            'Click on any transaction to view full details',
          ],
        },
        {
          title: 'Check Communication History',
          description: 'Review all communications sent to and received from the customer in the Activity tab.',
        },
        {
          title: 'Monitor Promises to Pay',
          description: 'Track payment commitments and their status in the Promises tab.',
        },
      ],
    },
    faq: [
      {
        question: 'How do I send a communication to this customer?',
        answer: 'Click the "Send Communication" button in the top-right corner. You can choose from email templates or create a custom message.',
      },
      {
        question: 'Can I update customer information?',
        answer: 'Yes, click the "Edit" button in the customer information panel to update details like contact information or credit limit.',
      },
      {
        question: 'How do I record a promise to pay?',
        answer: 'Click "Create Promise" in the Promises tab or use the quick action button in the top toolbar.',
      },
    ],
  },
  transactions: {
    title: 'Transactions',
    functionality: {
      overview: 'The Transactions page displays all financial transactions including invoices, payments, credits, and adjustments across all customers.',
      keyFeatures: [
        'Complete transaction history across all customers',
        'Advanced filtering by date, customer, amount, and status',
        'Transaction status tracking (Open, Paid, Overdue, Disputed)',
        'Quick access to transaction details',
        'Export capabilities for reporting',
      ],
    },
    userGuide: {
      gettingStarted: 'Use the Transactions page to monitor all financial activities. Filter by status to focus on specific transaction types.',
      steps: [
        {
          title: 'Filter Transactions',
          description: 'Use the filter options to narrow down transactions by date range, customer, status, or amount.',
        },
        {
          title: 'Review Transaction Details',
          description: 'Click on any transaction to view complete details including line items and payment history.',
        },
        {
          title: 'Export Data',
          description: 'Use the Export button to download transaction data for external reporting or analysis.',
        },
      ],
    },
    faq: [
      {
        question: 'What do the different transaction statuses mean?',
        answer: 'Open: Unpaid invoice within terms. Overdue: Payment past due date. Paid: Fully paid. Disputed: Customer has raised a dispute. Partial: Partially paid.',
      },
      {
        question: 'How do I mark a transaction as disputed?',
        answer: 'Click on the transaction and select "Create Dispute" from the actions menu.',
      },
    ],
  },
  payments: {
    title: 'Payments Dashboard',
    functionality: {
      overview: 'The Payments Dashboard provides an overview of all payment-related activities including receipts, allocations, and reconciliation.',
      keyFeatures: [
        'Payment processing and recording',
        'Receipt management and allocation',
        'Unapplied payments tracking',
        'Payment exceptions handling',
        'Reconciliation tools',
      ],
    },
    userGuide: {
      gettingStarted: 'Navigate through different payment functions using the menu. Start with Receipts to view incoming payments.',
      steps: [
        {
          title: 'View Receipts',
          description: 'Access the Receipts section to see all incoming payments.',
        },
        {
          title: 'Allocate Payments',
          description: 'Use the Allocation Workspace to match payments with invoices.',
        },
        {
          title: 'Handle Exceptions',
          description: 'Review and resolve payment exceptions in the Exceptions section.',
        },
      ],
    },
    faq: [
      {
        question: 'What is an unapplied payment?',
        answer: 'An unapplied payment is a receipt that has not yet been allocated to specific invoices.',
      },
      {
        question: 'How do I record a manual payment?',
        answer: 'Navigate to Payments > Record Payment and enter the payment details.',
      },
    ],
  },
  receipts: {
    title: 'Receipts',
    functionality: {
      overview: 'Manage all incoming payment receipts, track their status, and allocate them to outstanding invoices.',
      keyFeatures: [
        'View all payment receipts',
        'Track receipt status (Pending, Allocated, Partially Allocated)',
        'Quick allocation to invoices',
        'Receipt search and filtering',
      ],
    },
    userGuide: {
      gettingStarted: 'The Receipts page shows all incoming payments. Use filters to find specific receipts or view unallocated payments.',
      steps: [
        {
          title: 'Review Receipts',
          description: 'Browse through all receipts and check their allocation status.',
        },
        {
          title: 'Allocate Receipt',
          description: 'Click "Allocate" on any receipt to match it with outstanding invoices.',
        },
      ],
    },
    faq: [
      {
        question: 'What does "Partially Allocated" mean?',
        answer: 'A receipt is partially allocated when only part of the payment amount has been matched to invoices, leaving a remaining balance.',
      },
    ],
  },
  'ocr-remittance': {
    title: 'OCR Remittance',
    functionality: {
      overview: 'Use OCR (Optical Character Recognition) technology to automatically extract payment information from remittance advices and documents.',
      keyFeatures: [
        'Upload remittance documents',
        'Automatic data extraction using OCR',
        'Review and validate extracted data',
        'Quick payment allocation',
      ],
    },
    userGuide: {
      gettingStarted: 'Upload remittance documents to automatically extract payment details and allocate them to invoices.',
      steps: [
        {
          title: 'Upload Document',
          description: 'Drag and drop or select remittance documents to upload.',
        },
        {
          title: 'Review Extracted Data',
          description: 'Verify the automatically extracted payment information.',
        },
        {
          title: 'Allocate Payment',
          description: 'Confirm the allocation to complete the payment processing.',
        },
      ],
    },
    faq: [
      {
        question: 'What file formats are supported?',
        answer: 'PDF, JPG, PNG, and TIFF formats are supported for OCR processing.',
      },
    ],
  },
  'direct-debits': {
    title: 'Direct Debits',
    functionality: {
      overview: 'Manage direct debit mandates and automated payment collections from customer bank accounts.',
      keyFeatures: [
        'Direct debit mandate management',
        'Automated payment scheduling',
        'Failed payment tracking',
        'Mandate status monitoring',
      ],
    },
    userGuide: {
      gettingStarted: 'Set up and manage direct debit mandates for automated payment collection.',
      steps: [
        {
          title: 'Create Mandate',
          description: 'Set up a new direct debit mandate with customer bank details.',
        },
        {
          title: 'Schedule Collections',
          description: 'Configure automatic payment collection schedules.',
        },
      ],
    },
    faq: [
      {
        question: 'How long does mandate setup take?',
        answer: 'Direct debit mandates typically take 3-5 business days to become active after customer authorization.',
      },
    ],
  },
  'card-payments': {
    title: 'Card Payments',
    functionality: {
      overview: 'Process and manage credit and debit card payments from customers.',
      keyFeatures: [
        'Secure card payment processing',
        'Payment link generation',
        'Transaction history',
        'Refund management',
      ],
    },
    userGuide: {
      gettingStarted: 'Process card payments securely or send payment links to customers.',
      steps: [
        {
          title: 'Process Payment',
          description: 'Enter card details to process a payment immediately.',
        },
        {
          title: 'Send Payment Link',
          description: 'Generate and send a secure payment link to customers.',
        },
      ],
    },
    faq: [
      {
        question: 'Are card payments secure?',
        answer: 'Yes, all card payments are processed through PCI-DSS compliant payment gateways with end-to-end encryption.',
      },
    ],
  },
  'allocation-workspace': {
    title: 'Allocation Workspace',
    functionality: {
      overview: 'Match payments with outstanding invoices in an intuitive workspace designed for efficient payment allocation.',
      keyFeatures: [
        'Visual payment allocation interface',
        'Automatic matching suggestions',
        'Partial payment allocation',
        'Multi-invoice allocation',
      ],
    },
    userGuide: {
      gettingStarted: 'The Allocation Workspace helps you match payments to invoices. The system suggests matches based on amount and customer.',
      steps: [
        {
          title: 'Review Payment Details',
          description: 'Check the payment amount and customer information.',
        },
        {
          title: 'Select Invoices',
          description: 'Choose which invoices to allocate the payment against.',
        },
        {
          title: 'Confirm Allocation',
          description: 'Review and confirm the allocation to complete the process.',
        },
      ],
    },
    faq: [
      {
        question: 'Can I allocate one payment to multiple invoices?',
        answer: 'Yes, you can split a payment across multiple invoices in the allocation workspace.',
      },
    ],
  },
  'record-payment': {
    title: 'Record Payment',
    functionality: {
      overview: 'Manually record payments received outside the system, such as cash or check payments.',
      keyFeatures: [
        'Manual payment entry',
        'Multiple payment methods',
        'Immediate allocation option',
        'Payment reference tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'Use this form to record payments received through channels not automatically captured by the system.',
      steps: [
        {
          title: 'Enter Payment Details',
          description: 'Fill in the payment amount, date, method, and reference.',
        },
        {
          title: 'Select Customer',
          description: 'Choose the customer who made the payment.',
        },
        {
          title: 'Allocate (Optional)',
          description: 'Optionally allocate the payment to specific invoices immediately.',
        },
      ],
    },
    faq: [
      {
        question: 'What payment methods can I record?',
        answer: 'You can record cash, check, bank transfer, and other payment methods.',
      },
    ],
  },
  'payment-history': {
    title: 'Payment History',
    functionality: {
      overview: 'View complete history of all payments received, including allocation details and payment methods.',
      keyFeatures: [
        'Comprehensive payment records',
        'Payment method breakdown',
        'Allocation history',
        'Export capabilities',
      ],
    },
    userGuide: {
      gettingStarted: 'Browse through all historical payments with detailed allocation information.',
      steps: [
        {
          title: 'Filter Payments',
          description: 'Use date range and customer filters to find specific payments.',
        },
        {
          title: 'View Details',
          description: 'Click on any payment to see full allocation and transaction details.',
        },
      ],
    },
    faq: [
      {
        question: 'How far back does payment history go?',
        answer: 'Payment history is retained for the duration specified in your company settings, typically 7 years for compliance.',
      },
    ],
  },
  'unapplied-payments': {
    title: 'Unapplied Payments',
    functionality: {
      overview: 'Manage payments that have been received but not yet allocated to specific invoices.',
      keyFeatures: [
        'List of all unapplied payments',
        'Quick allocation tools',
        'Aging of unapplied amounts',
        'Customer-wise grouping',
      ],
    },
    userGuide: {
      gettingStarted: 'Review and allocate payments that are currently unmatched to invoices.',
      steps: [
        {
          title: 'Review Unapplied Payments',
          description: 'Check the list of payments awaiting allocation.',
        },
        {
          title: 'Allocate Payment',
          description: 'Click "Allocate" to match the payment with invoices.',
        },
      ],
    },
    faq: [
      {
        question: 'Why would a payment be unapplied?',
        answer: 'Payments may be unapplied if they were received without clear invoice references or if automatic matching failed.',
      },
    ],
  },
  'payment-exceptions': {
    title: 'Payment Exceptions',
    functionality: {
      overview: 'Handle payment exceptions such as overpayments, underpayments, and payment discrepancies.',
      keyFeatures: [
        'Exception identification',
        'Resolution workflows',
        'Exception categorization',
        'Audit trail',
      ],
    },
    userGuide: {
      gettingStarted: 'Review and resolve payment exceptions to maintain accurate accounts.',
      steps: [
        {
          title: 'Identify Exception',
          description: 'Review the exception type and details.',
        },
        {
          title: 'Resolve Exception',
          description: 'Choose the appropriate resolution action.',
        },
      ],
    },
    faq: [
      {
        question: 'What causes payment exceptions?',
        answer: 'Exceptions occur when payment amounts don\'t match invoice amounts, or when allocation rules cannot be automatically applied.',
      },
    ],
  },
  reconciliation: {
    title: 'Reconciliation',
    functionality: {
      overview: 'Reconcile bank statements with recorded payments to ensure accuracy and completeness.',
      keyFeatures: [
        'Bank statement import',
        'Automatic matching',
        'Discrepancy identification',
        'Reconciliation reports',
      ],
    },
    userGuide: {
      gettingStarted: 'Import bank statements and match them with recorded payments.',
      steps: [
        {
          title: 'Import Statement',
          description: 'Upload your bank statement file.',
        },
        {
          title: 'Review Matches',
          description: 'Check automatically matched transactions.',
        },
        {
          title: 'Resolve Discrepancies',
          description: 'Manually match or investigate unmatched items.',
        },
      ],
    },
    faq: [
      {
        question: 'What file formats are supported for bank statements?',
        answer: 'CSV, OFX, and QIF formats are commonly supported. Check with your administrator for specific formats.',
      },
    ],
  },
  'collections-dashboard': {
    title: 'Collections Dashboard',
    functionality: {
      overview: 'Monitor and manage collection activities, cases, and performance metrics.',
      keyFeatures: [
        'Collection case overview',
        'Performance metrics',
        'Priority case identification',
        'Team activity tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'The Collections Dashboard provides insights into your collection activities and case status.',
      steps: [
        {
          title: 'Review Metrics',
          description: 'Check key collection performance indicators.',
        },
        {
          title: 'Monitor Cases',
          description: 'View active cases and their status.',
        },
      ],
    },
    faq: [
      {
        question: 'How are collection priorities determined?',
        answer: 'Priorities are based on factors like amount overdue, customer risk, and days past due.',
      },
    ],
  },
  'collections-queue': {
    title: 'Collections Queue',
    functionality: {
      overview: 'Manage your collection workload with a prioritized queue of accounts requiring action.',
      keyFeatures: [
        'Prioritized collection queue',
        'Case assignment',
        'Quick actions',
        'Status tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'Work through your collection queue systematically, starting with highest priority cases.',
      steps: [
        {
          title: 'Review Queue',
          description: 'Check cases assigned to you or your team.',
        },
        {
          title: 'Take Action',
          description: 'Contact customers, record notes, or create promises.',
        },
        {
          title: 'Update Status',
          description: 'Mark cases as contacted or resolved.',
        },
      ],
    },
    faq: [
      {
        question: 'How is the queue prioritized?',
        answer: 'The queue is prioritized based on collection strategy rules, amount overdue, and customer risk level.',
      },
    ],
  },
  'case-details': {
    title: 'Case Details',
    functionality: {
      overview: 'View and manage detailed information about a specific collection case.',
      keyFeatures: [
        'Complete case history',
        'Action timeline',
        'Communication records',
        'Promise tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'The Case Details page shows all information related to a collection case.',
      steps: [
        {
          title: 'Review Case Information',
          description: 'Check case status, assigned user, and key details.',
        },
        {
          title: 'View Activity',
          description: 'Review all actions taken on this case.',
        },
        {
          title: 'Take Action',
          description: 'Add notes, send communications, or update case status.',
        },
      ],
    },
    faq: [
      {
        question: 'Can I reassign a case to another user?',
        answer: 'Yes, use the "Reassign" button to transfer the case to another team member.',
      },
    ],
  },
  promises: {
    title: 'Promises to Pay',
    functionality: {
      overview: 'Track and manage payment promises made by customers.',
      keyFeatures: [
        'Promise tracking',
        'Due date monitoring',
        'Broken promise alerts',
        'Promise fulfillment tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'Monitor all payment promises and their status.',
      steps: [
        {
          title: 'View Promises',
          description: 'See all active promises and their due dates.',
        },
        {
          title: 'Track Status',
          description: 'Monitor which promises are kept, broken, or upcoming.',
        },
        {
          title: 'Follow Up',
          description: 'Take action on broken or overdue promises.',
        },
      ],
    },
    faq: [
      {
        question: 'What happens when a promise is broken?',
        answer: 'Broken promises trigger alerts and may escalate the collection case based on your strategy rules.',
      },
    ],
  },
  'ageing-analysis': {
    title: 'Ageing Analysis',
    functionality: {
      overview: 'Analyze the aging of outstanding receivables across different time periods.',
      keyFeatures: [
        'Aging bucket analysis',
        'Trend visualization',
        'Customer-wise breakdown',
        'Export capabilities',
      ],
    },
    userGuide: {
      gettingStarted: 'Use aging analysis to understand the distribution of your receivables.',
      steps: [
        {
          title: 'Review Aging Buckets',
          description: 'Check amounts in each aging category.',
        },
        {
          title: 'Analyze Trends',
          description: 'Compare current aging with historical data.',
        },
        {
          title: 'Drill Down',
          description: 'Click on any bucket to see detailed customer breakdown.',
        },
      ],
    },
    faq: [
      {
        question: 'What are the standard aging buckets?',
        answer: 'Standard buckets are: Current (0-30 days), 31-60 days, 61-90 days, 91-120 days, and 120+ days.',
      },
    ],
  },
  'disputes-dashboard': {
    title: 'Disputes Dashboard',
    functionality: {
      overview: 'Monitor and manage customer disputes regarding invoices and charges.',
      keyFeatures: [
        'Dispute overview',
        'Status tracking',
        'Resolution metrics',
        'Aging of disputes',
      ],
    },
    userGuide: {
      gettingStarted: 'Track all customer disputes and their resolution status.',
      steps: [
        {
          title: 'Review Active Disputes',
          description: 'Check disputes requiring attention.',
        },
        {
          title: 'Monitor Resolution',
          description: 'Track progress on dispute resolution.',
        },
      ],
    },
    faq: [
      {
        question: 'How long does dispute resolution typically take?',
        answer: 'Resolution time varies by dispute type and complexity, typically ranging from a few days to several weeks.',
      },
    ],
  },
  'disputes-all': {
    title: 'All Disputes',
    functionality: {
      overview: 'View and manage all disputes across all customers and statuses.',
      keyFeatures: [
        'Complete dispute list',
        'Advanced filtering',
        'Bulk actions',
        'Status updates',
      ],
    },
    userGuide: {
      gettingStarted: 'Browse all disputes with filtering and search capabilities.',
      steps: [
        {
          title: 'Filter Disputes',
          description: 'Use filters to find specific disputes.',
        },
        {
          title: 'Update Status',
          description: 'Change dispute status as resolution progresses.',
        },
      ],
    },
    faq: [
      {
        question: 'Can I create a dispute on behalf of a customer?',
        answer: 'Yes, use the "Create Dispute" button to log a dispute reported by phone or email.',
      },
    ],
  },
  'credit-scoring': {
    title: 'Credit Scoring & Risk Assessment',
    functionality: {
      overview: 'Assess customer creditworthiness and manage credit limits based on risk analysis.',
      keyFeatures: [
        'Credit score calculation',
        'Risk assessment',
        'Credit limit recommendations',
        'Score history tracking',
      ],
    },
    userGuide: {
      gettingStarted: 'Review customer credit scores and adjust credit limits based on risk assessment.',
      steps: [
        {
          title: 'View Credit Scores',
          description: 'Check credit scores for all customers.',
        },
        {
          title: 'Assess Risk',
          description: 'Review risk factors and payment history.',
        },
        {
          title: 'Adjust Limits',
          description: 'Update credit limits based on assessment.',
        },
      ],
    },
    faq: [
      {
        question: 'How is the credit score calculated?',
        answer: 'Credit scores are calculated using payment history, outstanding balances, credit utilization, and external credit data when available.',
      },
    ],
  },
  'settings-company': {
    title: 'Company Settings',
    functionality: {
      overview: 'Configure company-wide settings including business information, currencies, and system preferences.',
      keyFeatures: [
        'Company information management',
        'Currency settings',
        'Payment terms configuration',
        'System preferences',
      ],
    },
    userGuide: {
      gettingStarted: 'Update your company settings to customize the system for your business needs.',
      steps: [
        {
          title: 'Update Company Info',
          description: 'Enter or modify company details.',
        },
        {
          title: 'Configure Currencies',
          description: 'Set up currencies used in your business.',
        },
        {
          title: 'Set Preferences',
          description: 'Customize system behavior and defaults.',
        },
      ],
    },
    faq: [
      {
        question: 'Who can modify company settings?',
        answer: 'Only users with Administrator role can modify company settings.',
      },
    ],
  },
  'settings-templates': {
    title: 'Document Templates',
    functionality: {
      overview: 'Create and manage email and document templates for customer communications.',
      keyFeatures: [
        'Template creation and editing',
        'Variable insertion',
        'Template preview',
        'Template categorization',
      ],
    },
    userGuide: {
      gettingStarted: 'Create templates for common communications to save time and ensure consistency.',
      steps: [
        {
          title: 'Create Template',
          description: 'Click "Create Template" to start a new template.',
        },
        {
          title: 'Add Content',
          description: 'Write your template content and insert variables.',
        },
        {
          title: 'Preview and Save',
          description: 'Preview the template and save it for use.',
        },
      ],
    },
    faq: [
      {
        question: 'What variables can I use in templates?',
        answer: 'You can use customer name, invoice details, amounts, dates, and many other dynamic fields.',
      },
    ],
  },
  'settings-strategies': {
    title: 'Collection Strategies',
    functionality: {
      overview: 'Define and manage automated collection strategies with rules and actions.',
      keyFeatures: [
        'Strategy creation',
        'Rule configuration',
        'Action scheduling',
        'Strategy assignment',
      ],
    },
    userGuide: {
      gettingStarted: 'Create collection strategies to automate your collection process.',
      steps: [
        {
          title: 'Create Strategy',
          description: 'Define a new collection strategy with rules.',
        },
        {
          title: 'Add Actions',
          description: 'Configure actions to be taken at different stages.',
        },
        {
          title: 'Assign to Customers',
          description: 'Apply the strategy to customer segments.',
        },
      ],
    },
    faq: [
      {
        question: 'Can I have multiple strategies?',
        answer: 'Yes, you can create different strategies for different customer segments or risk levels.',
      },
    ],
  },
  'settings-dispute-types': {
    title: 'Dispute Types',
    functionality: {
      overview: 'Configure dispute types and categories for consistent dispute management.',
      keyFeatures: [
        'Dispute type definition',
        'Category management',
        'Resolution workflows',
      ],
    },
    userGuide: {
      gettingStarted: 'Define dispute types to categorize and track disputes effectively.',
      steps: [
        {
          title: 'Create Dispute Type',
          description: 'Add a new dispute type with description.',
        },
        {
          title: 'Configure Workflow',
          description: 'Set up resolution workflow for the dispute type.',
        },
      ],
    },
    faq: [
      {
        question: 'What are common dispute types?',
        answer: 'Common types include pricing disputes, quality issues, delivery problems, and billing errors.',
      },
    ],
  },
  'settings-user-management': {
    title: 'User Management',
    functionality: {
      overview: 'Manage system users, their roles, and access permissions.',
      keyFeatures: [
        'User creation and editing',
        'Role assignment',
        'Access control',
        'User status management',
      ],
    },
    userGuide: {
      gettingStarted: 'Add and manage users who will access the credit control system.',
      steps: [
        {
          title: 'Add User',
          description: 'Create a new user account with email and details.',
        },
        {
          title: 'Assign Role',
          description: 'Select the appropriate role for the user.',
        },
        {
          title: 'Set Permissions',
          description: 'Configure specific permissions if needed.',
        },
      ],
    },
    faq: [
      {
        question: 'How do I deactivate a user?',
        answer: 'Click on the user and change their status to "Inactive". This preserves their history while preventing login.',
      },
    ],
  },
  'settings-roles-permissions': {
    title: 'Roles & Permissions',
    functionality: {
      overview: 'Define roles and configure granular permissions for system access control.',
      keyFeatures: [
        'Role creation',
        'Permission configuration',
        'Access level management',
        'Role templates',
      ],
    },
    userGuide: {
      gettingStarted: 'Create roles with specific permissions to control user access.',
      steps: [
        {
          title: 'Create Role',
          description: 'Define a new role with a descriptive name.',
        },
        {
          title: 'Set Permissions',
          description: 'Configure what actions this role can perform.',
        },
        {
          title: 'Assign to Users',
          description: 'Apply the role to appropriate users.',
        },
      ],
    },
    faq: [
      {
        question: 'What are the default roles?',
        answer: 'Default roles typically include Administrator, Manager, Collector, and Viewer with predefined permissions.',
      },
    ],
  },
  'settings-activity-log': {
    title: 'User Activity Log',
    functionality: {
      overview: 'Monitor and audit user activities within the system for security and compliance.',
      keyFeatures: [
        'Activity tracking',
        'User action history',
        'Audit trail',
        'Export capabilities',
      ],
    },
    userGuide: {
      gettingStarted: 'Review user activities to monitor system usage and maintain security.',
      steps: [
        {
          title: 'Filter Activities',
          description: 'Use filters to find specific user actions.',
        },
        {
          title: 'Review Details',
          description: 'Click on any activity to see full details.',
        },
        {
          title: 'Export Log',
          description: 'Download activity logs for compliance reporting.',
        },
      ],
    },
    faq: [
      {
        question: 'How long are activity logs retained?',
        answer: 'Activity logs are typically retained for 12 months or as configured in your compliance settings.',
      },
    ],
  },
  'settings-ai-configuration': {
    title: 'AI Configuration',
    functionality: {
      overview: 'Configure AI-powered features including smart actions, predictions, and automation.',
      keyFeatures: [
        'AI feature enablement',
        'Model configuration',
        'Prediction settings',
        'Automation rules',
      ],
    },
    userGuide: {
      gettingStarted: 'Enable and configure AI features to enhance collection efficiency.',
      steps: [
        {
          title: 'Enable Features',
          description: 'Turn on desired AI-powered features.',
        },
        {
          title: 'Configure Settings',
          description: 'Adjust AI behavior and thresholds.',
        },
        {
          title: 'Monitor Performance',
          description: 'Review AI recommendations and accuracy.',
        },
      ],
    },
    faq: [
      {
        question: 'What AI features are available?',
        answer: 'AI features include payment prediction, smart action recommendations, optimal contact time suggestions, and automated case prioritization.',
      },
    ],
  },
  integrations: {
    title: 'System Integrations',
    functionality: {
      overview: 'Manage integrations with external systems including accounting software, payment gateways, and communication platforms.',
      keyFeatures: [
        'Integration setup',
        'Connection management',
        'Data synchronization',
        'Integration monitoring',
      ],
    },
    userGuide: {
      gettingStarted: 'Connect external systems to automate data flow and enhance functionality.',
      steps: [
        {
          title: 'Select Integration',
          description: 'Choose the system you want to integrate.',
        },
        {
          title: 'Configure Connection',
          description: 'Enter credentials and connection details.',
        },
        {
          title: 'Test Connection',
          description: 'Verify the integration is working correctly.',
        },
      ],
    },
    faq: [
      {
        question: 'What systems can be integrated?',
        answer: 'Common integrations include QuickBooks, Xero, Sage, payment gateways, and email platforms.',
      },
      {
        question: 'How often does data sync?',
        answer: 'Data synchronization frequency depends on the integration type, typically ranging from real-time to daily updates.',
      },
    ],
  },
};
